import * as React from "react";
import moment from 'moment';
// Amplify UI
import IconStreamingNow from './../../../assets/icon-streaming-now.svg';
import IconStreamingToday from './../../../assets/icon-streaming-today.svg';
import IconStreamingUpcoming from './../../../assets/icon-streaming-upcoming.svg';
import IconStreamingPast from './../../../assets/icon-streaming-past.svg';
import SponsorLogoDefault from "./../../../assets/default.png";
import LivestreamDefaultBackground from "./../../../assets/livestream-default-background.png";
import * as Styled from "./BigCard.styled";
import { ILiveStream } from "../../../types";
import { PlayFill } from 'react-bootstrap-icons';
import { colors } from "../../../libs/themes";

export const BigCard: React.FC<{
    event: ILiveStream;
    hasEligiblePlan: boolean;
    onWatchClick: (event: ILiveStream) => void;
    isSignedIn?: boolean;
    customHeight?: string;
    customBorderRadius?: string;
}> = ({ event, onWatchClick, isSignedIn, hasEligiblePlan, customHeight,  customBorderRadius}) => {

    const LABEL_WATCH_NOW = 'Watch Now';
    const LABEL_GET_ACCESS = 'Get Access';
    const LABEL_PURCHASE_PLAN = "Purchase Plan";
    const LABEL_STREAMING_SOON = 'Streaming Soon';
    const LABEL_UPCOMING = 'Upcoming';

    const isNow = React.useCallback((dateStart: string, dateEnd: string) => {
        const now = moment(new Date(), moment.ISO_8601);
        const eventDateStart = moment(dateStart, moment.ISO_8601);
        const eventDateEnd = dateEnd ? moment(dateEnd, moment.ISO_8601) : moment(eventDateStart).add({ hours: 1 });
        return moment(now).isBetween(eventDateStart, eventDateEnd);
    }, []);

    const isToday = React.useCallback((date: string) => {
        const today = moment(new Date(), moment.ISO_8601);
        const eventDate = moment(date, moment.ISO_8601);
        return moment(eventDate).isSame(today, 'day');
    }, []);

    const isFuture = React.useCallback((date: string) => {
        const today = moment(new Date(), moment.ISO_8601);
        const eventDate = moment(date, moment.ISO_8601);
        return moment(eventDate).isAfter(today, 'day');
    }, []);

    const isPast = React.useCallback((date: string) => {
        const today = moment(new Date(), moment.ISO_8601);
        const eventDate = moment(date, moment.ISO_8601);
        return moment(eventDate).isBefore(today, 'day');
    }, []);

    const isEventNow = React.useMemo(() => {
        return isNow(event.eventStartDate, event.eventEndDate) || event.streamingUrl;
    }, [event, isNow]);

    const isEventToday = React.useMemo(() => {
        return isToday(event.date);
    }, [event.date, isToday]);

    const isEventFuture = React.useMemo(() => {
        return isFuture(event.date);
    }, [event.date, isFuture]);

    const isEventPast = React.useMemo(() => {
        return isPast(event.date);
    }, [event.date, isPast]);

    const cheapPackageName = React.useMemo(() => {
        let _arr = event.planDetails;
        _arr.sort((a, b) => a.amount - b.amount);
        return _arr[0]?.name || '';
    }, [event.planDetails]);

    const eventStatus = React.useMemo(() => {
        let _status;

        if (event.isPrivate) {

            if (isSignedIn) {

                if (hasEligiblePlan) {

                    if (isEventNow) {
                        _status = LABEL_WATCH_NOW;
                    } else if (isEventToday) {
                        _status = LABEL_STREAMING_SOON;
                    } else if (isEventFuture) {
                        _status = LABEL_UPCOMING;
                    } else {
                        _status = LABEL_WATCH_NOW;
                    }

                } else {
                    _status = LABEL_PURCHASE_PLAN;
                }

            } else {
                _status = LABEL_GET_ACCESS;
            }

        } else {

            if (isEventNow) {
                _status = LABEL_WATCH_NOW;
            } else if (isEventToday) {
                _status = LABEL_STREAMING_SOON;
            } else if (isEventFuture) {
                _status = LABEL_UPCOMING;
            } else {
                _status = LABEL_WATCH_NOW;
            }

        }

        return _status;
    }, [
        event,
        isSignedIn,
        hasEligiblePlan,
        isEventNow,
        isEventToday,   
        isEventFuture
    ]);

    return (
        <Styled.MediaCard className={`media-card`}>
            <Styled.MediaCardWrapper className="media-card--wrapper"  customHeight={customHeight} customBorderRadius={customBorderRadius}>
                <Styled.MediaPoster className="media-poster" backgroundImage={LivestreamDefaultBackground}>
                    <Styled.TrailerOverlay>
                        <Styled.MetaContent>
                            <Styled.NewInfoRow>
                                <Styled.InfoItem>
                                    <div className={`${isEventNow ? "is-now"
                                        : isEventToday ? "is-today"
                                            : isEventFuture ? "is-upcoming"
                                                : isEventPast ? "is-past"
                                                    : ""} ${event.isPrivate ? "is-private" : ""} event-happens-badge`}>
                                        {isEventNow ? (<img src={IconStreamingNow} height="26" style={{ maxHeight: '26px' }} alt="Now" />)
                                            : isEventToday ? (<img src={IconStreamingToday} height="22" style={{ maxHeight: '22px' }} alt="Today" />)
                                                : isEventFuture ? (<img src={IconStreamingUpcoming} height="22" style={{ maxHeight: '22px' }} alt="Upcoming" />)
                                                    : isEventPast ? (<img src={IconStreamingPast} height="22" style={{ maxHeight: '22px' }} alt="Past" />)
                                                        : ""}
                                    </div>
                                    <Styled.Divider>|</Styled.Divider>
                                </Styled.InfoItem>

                                <Styled.InfoItem
                                    className={`${isEventNow ? "is-now"
                                        : isEventToday ? "is-today"
                                            : isEventFuture ? "is-upcoming"
                                                : isEventPast ? "is-past"
                                                    : ""} ${event.isPrivate ? "is-private" : ""} event-date`}>

                                    <span data-date={event.date} style={{ lineHeight: "1.125", whiteSpace: "nowrap" }}>
                                        {moment(new Date(event.date)).format('MMM Do, YYYY')}
                                        <span style={{ display: "inline-block", width: "8px" }}></span>
                                        {moment(new Date(event.date)).format('h:mmA')}
                                    </span>

                                </Styled.InfoItem>
                            </Styled.NewInfoRow>
                            <Styled.Title title={event.title}>{event.title}</Styled.Title>


                            <Styled.MediaTextWrapperNewInfoItem>
                                <Styled.Description>
                                    {event.isPrivate && (
                                        <span style={{ lineHeight: '1.25' }}>
                                            Available with <strong style={{ fontWeight: '700' }}>{cheapPackageName}</strong>.<br />
                                            Start watching now!
                                        </span>
                                    )}
                                </Styled.Description>
                            </Styled.MediaTextWrapperNewInfoItem>
                            <Styled.WatchNowButton
                                className={`${isEventNow ? "is-now"
                                    : isEventToday ? "is-today"
                                        : isEventFuture ? "is-upcoming"
                                            : isEventPast ? "is-past"
                                                : ""} ${event.isPrivate ? "is-private" : ""} event-button`}
                                onClick={() => onWatchClick(event)}
                            >
                                {eventStatus}
                                 <PlayFill color={`${colors.text.white}`} size={20} />
                            </Styled.WatchNowButton>
                        </Styled.MetaContent>
                    </Styled.TrailerOverlay>

                    <Styled.MediaPosterArt className="media-poster--art">
                        <Styled.MediaPosterImage
                           src={event.thumbnailUrl ? event.thumbnailUrl : SponsorLogoDefault}
                          // src="https://t4.ftcdn.net/jpg/05/18/69/85/360_F_518698520_Xk9tIwoYpyX6kkVsF6GpQ1z7sKXO8YRz.jpg"
                            className="media-poster--image"
                            alt="Event Poster"
                        />
                    </Styled.MediaPosterArt>
                </Styled.MediaPoster>

                <Styled.MediaAncillary className="media-ancillary">
                    <Styled.MediaMetadata className="media-metadata">
                        <div style={{ color: 'rgba(255,255,255, 1)' }}>{event.title}</div>
                    </Styled.MediaMetadata>
                </Styled.MediaAncillary>
            </Styled.MediaCardWrapper>
        </Styled.MediaCard>

    );
};
